/**
 * WDModelItems - Transparent Reactive List Component
 * 
 * Manages three separate concerns:
 * 1. #model (WDModelArray) - The backing data model (owned by this component)
 * 2. #fetch (WDFetch) - Optional remote data source
 * 3. #uriModel (WDModel) - Optional SHARED model for URI template resolution
 * 
 * CRITICAL: #uriModel is often a SHARED reference to a parent's model.
 * Must unsubscribe from its events immediately on disconnect to prevent
 * race conditions where the parent model triggers fetches after this
 * component is removed but before deferred cleanup completes.
 */

import WDElement from './wd-element';
import WDUtil from '../core/wd-util';
import { WDModel, WDModelArray } from '../core/wd-model';

export default class WDModelItems extends WDElement
{
	#isFiltering = false;
	#isFetching = false;
	#model;
	#fetch = null;
	#filters = null;
	#uriModel = null;
	#uriModelUnsubscribe = null;  // Stores unsubscribe function for uriModel watcher
	#origReqURI = null;
	#config = {
		initFetch: true,
		data: null,
		fetch: null,
		method: 'GET',
		debugLogging: false,
		resultKey: 'items',
		filterKey: 'search_string',
		createItemHandler: null,
		updateItemHandler: null,
		templates: {
			label: '${label}',
			value: '${value}',
			icon: null,
		}
	};

	constructor(cfg)
	{
		super();

		WDUtil.merge(this.#config, cfg);

		//if we are monitoring a model for changes to property values that we
		//will then use to resolve templates in the fetch uri
		if (this.#config?.uriModel) this.uriModel = this.#config.uriModel;
		
		this.#model = new WDModelArray([]);
		if (this.#config?.debugLogging) this.debugLogging = this.#config.debugLogging;

		//setup event watchers for WDModelArray changes
		this.setupModelEventListeners();	
	}

	connectedCallback()
	{
		super.connectedCallback();

		if (this.#config?.fetch) 
		{
			this.fetch = this.#config.fetch;
		}
		else if (this.#config?.data)
		{
			this.model = this.#config.data;
		}	
	};

	setupModelEventListeners()
	{
		// Store unsubscribe functions for cleanup
		this._modelUnsubscribers = [];

		// Listen for individual changes (when not in batch mode)
		this._modelUnsubscribers.push(
			this.#model.on('set', evtData => {
				this.modelItemSet(evtData);

			})
		);

		this._modelUnsubscribers.push(
			this.#model.on('delete', evtData => {
				this.modelItemDelete(evtData);
			})
		);
		
		// Listen for batch operations (for performance optimization)
		this._modelUnsubscribers.push(
			this.#model.on('batch', evtData => {
				this.modelBatchCompleted(evtData);
			})
		);
	}

	//accessors
	get config() { return this.#config; };
	get items() { return this.children; };
	get itemArray() { return [...this.items]; };
	get firstItem() { return this.items[0]; };
	get lastItem() { return this.items[this.items.length - 1]; };

	set createItemHandler(val) { this.config.createItemHandler = val; };
	
	get isFetching() { return this.#isFetching; };
	get isFiltering() { return this.#isFiltering; };

	get uriModel() { return this.#uriModel; };
	set uriModel(val) { 
		this.#uriModel = val.isProxy ? val : new WDModel(val);
		this.addWatchers();
	};

	get model() { return this.#model; };
	set model(data) {

		// Guard: Don't set model if component has been cleaned up
		if (!this.#model) {
			console.warn('WDModelItems: Attempted to set model after cleanup', this);
			return;
		}
			
		this.model.replace(data);
		this.trigger('model:set',this.model);
	};

	get filters() { return this.#filters; };
	set filters(filters)
	{
		this.#filters = filters;
		filters.on('filter:apply', () => this.fetchModel());
	};

	reset()
	{
		// Use batch operation for performance
		this.#model.replace([]);
	}

	set fetchURI(uri)
	{
		this.fetch.setReqURI(uri);
		this.#origReqURI = this.fetch.getReqURI();
		this.fetchModel();
	};

	get fetch() { return this.#fetch; };
	set fetch(f)
	{
		this.#fetch = f;
		this.#origReqURI = f.getReqURI();
		
		//if set, run the fetch after we set it
		if (this.#config?.initFetch) this.fetchModel();
	};

	isURIValid(uri)
	{
		return (!uri.includes('${') &&
			!uri.includes('//') &&
			!uri.includes('/undefined') &&
			!uri.includes('/null')
		);
	};

	resolveReqURI()
	{
		if (!this.fetch) return false;

		let uri = this.#origReqURI;

		if (this.#uriModel !== null && uri.includes('${'))
		{
			uri = uri.interpolate(this.#uriModel);
		}

		if (this.isURIValid(uri))
		{
			this.fetch.setReqURI(uri);
			return true;
		}

		return false;
	};

	/**
	 * Add watchers to uriModel for dynamic URI template resolution
	 * 
	 * When uriModel properties change, attempts to resolve template variables
	 * in the fetch URI. If successful, triggers a fetch.
	 * 
	 * CRITICAL: Stores unsubscribe function for cleanup. The uriModel may be
	 * a SHARED reference to a parent's model, so we must unsubscribe cleanly.
	 */
	addWatchers()
	{
		if (!this.#uriModel) return;

		// Remove previous watcher if exists (e.g., if uriModel is reset)
		if (this.#uriModelUnsubscribe) {
			this.#uriModelUnsubscribe();
			this.#uriModelUnsubscribe = null;
		}

		// Add watcher and store unsubscribe function
		this.#uriModelUnsubscribe = this.#uriModel.on('change', evt => {

			if (!this.#origReqURI) return;

			// Determine which properties are used in the URI template
			const watchKeys = this.#origReqURI.getTemplateKeys();
			if (watchKeys.includes(evt.property))
			{
				// Property used in URI changed, attempt to resolve and fetch
				this.fetchModel();
			}

		});

	};

	fetchModel()
	{
		if ( this.resolveReqURI() )
		{
			this.trigger('model:fetch');
			this.#isFetching = true;

			if (this.#filters && this.#filters.value.length > 0)
			{
				if (this.fetch?.filters)
				{
					this.fetch.filters.replace(this.#filters.value);
				}
				else
				{
					this.fetch.filters = this.#filters.value;
				}
				
			}
			else if (this?.fetch?.filters)
			{
				delete this.fetch.filters;
			}

			return this.fetch.req(this.config.method).then(results =>
			{
				this.#isFetching = false;
				
				// Guard: Don't update model if component has been cleaned up
				if (!this.#model) {
					console.warn('WDModelItems: Fetch completed after cleanup, ignoring results');
					return;
				}
				
				this.trigger('model:fetched', results);

				if (Array.isArray(results)) this.model = results;
				else this.model = results[this.config.resultKey];
			});
		}
		else
		{
			return new Promise((resolve, reject) =>
			{
				this.model = [];
				resolve(true);
			});
		}
	};
	
	// Individual change handlers (fired when not in batch mode)
	modelItemSet(evtData)
	{
		/**
		 * create/replace a row with this index
		 */
		const idx = evtData.property;
		const value = evtData.value;

		//if the property isn't numeric, then it's an internal array property we don't want
		if (parseInt(idx) != idx) 
		{
			return;
		}

		//if value is undefined, the item is being removed
		if (typeof (value) == 'undefined')
		{
			if (this.items[idx]) 
			{
				this.items[idx].remove();
				this.trigger('item:removed');
			}
		}
		else if (this.items[idx])
		{
			//exists, pass the model to it so it can update
			this.updateModelItem(this.items[idx],value);
			this.trigger('item:updated',this.items[idx]);
		}
		else
		{
			const row = this.buildModelItem(value, idx);
			this.trigger('item:added', row);
		}

		this.handleItemsChanged();
	};

	modelItemDelete(evtData)
	{
		const idx = evtData.property;

		//if the property isn't numeric, then it's an internal array property we don't want
		if (parseInt(idx) != idx) return;

		const item = this.items[idx];
		if (item) {
			item.remove();
			this.handleItemsChanged();
			this.trigger('item:removed', item);
		}
	};

	// Batch operation handler (for performance optimization)
	modelBatchCompleted(evtData)
	{
		// Clear existing items (replaceChildren safely triggers disconnectedCallback)
		this.replaceChildren();

		// Add all model items
		this.#model.forEach((item, index) =>
		{
			const row = this.buildModelItem(item, index);
			this.trigger('item:added', row);
		});

		this.handleItemsChanged();
		this.trigger('items:added',this.#model);
	};

	handleItemsChanged()
	{
		if (this.model.length > 0) this.classList.add('has-results');
		else this.classList.remove('has-results');

		//catchall event for all changes to the item list
		//todo - why is model not decremented in count at this point, I had to set a timeout
		//for the model count to be updated for the event recipient.  
		setTimeout( () => this.trigger('items:updated'),0);
	}

	/**
	 * should be overridden to create the item, we don't do that here
	 * @param {*} data 
	 */
	createModelItem(data)
	{
		if (this.config?.createItemHandler) 
		{
			return this.config.createItemHandler(data);
		}
	}

	updateModelItem(item,data)
	{
		const newItem = this.createModelItem(data);
		if (!newItem) return null;

		item.before(newItem);
		item.remove();
	}

	buildModelItem(value, idx)
	{
		const item = this.createModelItem(value);
		if (!item) return null;

		if (idx < this.items.length - 1)
		{
			const existingItem = this.items[idx];
			existingItem.before(item);
			existingItem.remove();
		}
		else
		{
			this.append(item);
		}

		return item;
	}

	filter(str)
	{
		this.#isFiltering = str.length > 0;

		if (this.fetch)
		{
			const fk = this.config.filterKey;
			if (str.length > 0)
			{
				this.fetch[fk] = str;
			}
			else
			{
				delete (this.fetch[fk]);
			}

			this.fetchModel(true);
		}
		else if (this.model.length > 0)
		{
			const ss = str.toLowerCase();

			this.items.forEach((item, idx) =>
			{
				const modelData = this.#model[idx];
				const labelStr = this.#config.templates.label.interpolate(modelData).toLowerCase();
			
				if (ss.length == 0 || labelStr.includes(ss))
				{
					item.style.display = 'flex';
				}
				else
				{
					item.style.display = 'none';
				}
			});
		}
	};

	// Convenience methods for working with the model
	addItem(item)
	{
		this.model.push(item);
		return this;
	};

	removeItem(index)
	{
		if (index >= 0 && index < this.model.length) {
			this.model.splice(index, 1);
		}
		return this;
	};

	moveItem(fromIndex, toIndex)
	{
		this.model.move(fromIndex, toIndex);
		return this;
	};

	updateItem(index, data)
	{
		if (index >= 0 && index < this.model.length) {
			Object.assign(this.model[index], data);
		}
		return this;
	};

	// Batch operations for performance
	batchUpdate(callback)
	{
		return this.model.batch(callback);
	};

	// Utility methods
	findItemByProperty(property, value)
	{
		return this.model.find(item => item[property] === value);
	};

	findItemIndexByProperty(property, value)
	{
		return this.model.findIndex(item => item[property] === value);
	};

	/**
	 * Override disconnectedCallback to immediately stop listening to external models
	 */
	disconnectedCallback()
	{
		// Immediately unsubscribe from uriModel to prevent fetch triggers
		// This must happen BEFORE deferred cleanup to prevent race conditions
		if (this.#uriModelUnsubscribe) {
			this.#uriModelUnsubscribe();
			this.#uriModelUnsubscribe = null;
		}

		// Call parent to handle deferred cleanup pattern
		super.disconnectedCallback();
	}

	/**
	 * Cleanup callback - called when component is truly removed from DOM
	 * Inherits from WDElement's deferred cleanup pattern
	 */
	cleanupCallback()
	{
		// Call parent cleanup FIRST
		super.cleanupCallback();

		// Unsubscribe from model events BEFORE destroying
		if (this._modelUnsubscribers) {
			this._modelUnsubscribers.forEach(unsub => unsub());
			this._modelUnsubscribers = null;
		}
		
		// Destroy #model - we own this, safe to destroy
		if (this.#model) {
			this.#model.destroy();
			this.#model = null;
		}
		
		// Clear #uriModel reference but DON'T destroy it
		// CRITICAL: uriModel is often a SHARED reference to a parent component's
		// model (e.g., parent form's model). We don't own it, so we must NOT
		// call destroy() on it. Just dereference and let the parent manage its lifecycle.
		if (this.#uriModel) {
			this.#uriModel = null;  // Clear reference, parent still owns the model
		}
		
		// Clear fetch instance
		if (this.#fetch) {
			if (this.#fetch.abort) this.#fetch.abort();
			this.#fetch = null;
		}
		
		// Clear other references
		this.#filters = null;
		this.#origReqURI = null;
		this.#config = null;
	}
};

customElements.define('wd-model-items', WDModelItems);
